import React, { useState, useEffect } from "react";
import {
  LayoutDashboard,
  BookOpen,
  MessageCircle,
  Users,
  FileText,
  ChevronLeft,
  LogOut,
  Network,
  Settings,
  Loader2,
} from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "../ui/tooltip";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from "../ui/alert-dialog";
import { useNavigate } from "react-router";
import { useAuth } from "@duvi-agency/auth";
import { me } from "../../api/client";
import { useQuery } from "../../hooks/useApi";

interface SidebarProps {
  activeItem?: string;
  onNavigate?: (item: string) => void;
}

export function Sidebar({
  activeItem = "Dashboard",
  onNavigate,
}: SidebarProps) {
  // Sequential animation: collapse = fade text → shrink width, expand = grow width → fade text
  const [narrow, setNarrow] = useState(false); // Controls sidebar width
  const [textVisible, setTextVisible] = useState(true); // Controls text opacity
  const [textHidden, setTextHidden] = useState(false); // Controls display:none after fade

  const collapsed = narrow; // Alias for tooltip logic

  const handleToggle = () => {
    if (!narrow) {
      // Collapse: fade text out first, then shrink after fade completes
      setTextVisible(false);
      setTimeout(() => {
        setTextHidden(true);
        setNarrow(true);
      }, 200);
    } else {
      // Expand: grow width first, then restore text and fade in after width completes
      setNarrow(false);
      setTimeout(() => {
        setTextHidden(false);
        // Allow one frame for display:none removal before starting opacity transition
        requestAnimationFrame(() => setTextVisible(true));
      }, 300);
    }
  };

  const textClass = textVisible
    ? "opacity-100"
    : `opacity-0 overflow-hidden ${textHidden ? "hidden" : ""}`;

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();
  const { logout, authClient } = useAuth();
  const { data: profile, refetch: refetchProfile } = useQuery(() => me.get());
  const [avatarOverride, setAvatarOverride] = useState<string | null>(null);
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const resolvedAvatar = avatarOverride ?? profile?.avatar_url;
  const displayAvatar = avatarLoadFailed ? undefined : resolvedAvatar;

  useEffect(() => {
    const handleProfileUpdate = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.avatar_url) {
        setAvatarLoadFailed(false);
        setAvatarOverride(detail.avatar_url);
      } else if (detail?.avatar_url === undefined) {
        // Name change or avatar removal — clear override, rely on refetch.
        setAvatarOverride(null);
      }
      refetchProfile();
    };
    window.addEventListener("profile:updated", handleProfileUpdate);
    return () =>
      window.removeEventListener("profile:updated", handleProfileUpdate);
  }, [refetchProfile]);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    await authClient.logout();
    logout();
    // Hard-reload to the SPA root rather than navigate("/auth"). The bare
    // /auth path is owned by the edge → auth-API integration on the
    // app.<env>.duvi.uk distribution, so a hard refresh of /auth returns
    // S3 403 (no SPA fallback). The root route renders the login entry
    // directly, guaranteeing the SPA loads and the auth provider
    // reinitialises cleanly. Mirrors the bug-0038 fix in App.tsx.
    // See bug 0054.
    window.location.href = "/";
  };

  const menuSections = [
    {
      label: "OVERVIEW",
      items: [
        {
          id: "Dashboard",
          icon: LayoutDashboard,
          label: "Dashboard",
          path: "/dashboard",
        },
      ],
    },
    {
      label: "OPERATIONS",
      items: [
        {
          id: "Knowledge Base",
          icon: BookOpen,
          label: "Knowledge Base",
          path: "/knowledge-base",
        },
        {
          id: "Support",
          icon: MessageCircle,
          label: "Support",
          path: "/support/tickets",
        },
      ],
    },
    {
      label: "PLATFORM",
      items: [
        {
          id: "Niches",
          icon: Network,
          label: "Niches",
          path: "/platform/niches",
        },
        { id: "Users", icon: Users, label: "Users", path: "/user-management" },
        {
          id: "Audit Log",
          icon: FileText,
          label: "Audit Log",
          path: "/audit-log",
        },
      ],
    },
  ];

  const handleItemClick = (itemId: string, path?: string) => {
    if (path) {
      navigate(path);
    }
    if (onNavigate) {
      onNavigate(itemId);
    }
  };

  return (
    <div
      className="h-screen bg-duvi-surface border-r border-duvi-border flex flex-col transition-all duration-300"
      style={{
        width: narrow ? "var(--sidebar-collapsed)" : "var(--sidebar-width)",
      }}
    >
      {/* Logo / Header */}
      <div className="h-[var(--topbar-height)] flex items-center justify-between px-4 border-b border-duvi-border">
        <span
          className={`text-duvi-text-primary whitespace-nowrap transition-opacity duration-300 ${textClass}`}
          style={{
            fontSize: "var(--text-lg)",
            fontWeight: "var(--font-weight-semibold)",
          }}
        >
          Duvi Platform
        </span>
        <button
          onClick={handleToggle}
          className="p-1 hover:bg-duvi-elevated rounded transition-colors text-duvi-text-secondary"
        >
          <ChevronLeft
            className={`w-4 h-4 transition-transform ${narrow ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-4">
        {menuSections.map((section, idx) => (
          <div key={idx} className={idx > 0 ? "mt-4" : ""}>
            {section.items.map((item) => {
              const Icon = item.icon;
              const isActive = activeItem === item.id;

              const btn = (
                <button
                  onClick={() => handleItemClick(item.id, item.path)}
                  className={`w-full flex items-center gap-3 px-4 py-2 transition-colors relative ${
                    isActive
                      ? "bg-duvi-sidebar-active text-duvi-text-primary"
                      : "text-duvi-text-secondary hover:text-duvi-text-primary hover:bg-duvi-elevated"
                  }`}
                  style={{ fontSize: "var(--text-sm)" }}
                >
                  {isActive && (
                    <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-duvi-accent" />
                  )}
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span
                    className={`whitespace-nowrap transition-opacity duration-300 h-5 leading-5 ${textClass}`}
                  >
                    {item.label}
                  </span>
                </button>
              );

              if (collapsed) {
                return (
                  <Tooltip key={item.id}>
                    <TooltipTrigger asChild>{btn}</TooltipTrigger>
                    <TooltipContent side="right" sideOffset={8}>
                      {item.label}
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return <React.Fragment key={item.id}>{btn}</React.Fragment>;
            })}
          </div>
        ))}
      </div>

      {/* User Section */}
      <div className="border-t border-duvi-border p-4 relative">
        <button
          onClick={() => setUserMenuOpen(!userMenuOpen)}
          data-testid="sidebar-user-menu"
          className="w-full flex items-center gap-3 hover:bg-duvi-elevated rounded-[6px] p-1 -m-1 transition-colors"
        >
          {displayAvatar ? (
            <img
              src={displayAvatar}
              alt={profile?.name ?? "Profile"}
              className="rounded-full object-cover flex-shrink-0 transition-all duration-300"
              style={{
                width: narrow ? "20px" : "45px",
                height: narrow ? "20px" : "45px",
              }}
              onError={() => setAvatarLoadFailed(true)}
            />
          ) : (
            <div
              className="rounded-full bg-duvi-accent flex items-center justify-center text-white flex-shrink-0 transition-all duration-300"
              style={{
                width: narrow ? "20px" : "45px",
                height: narrow ? "20px" : "45px",
                fontSize: narrow ? "8px" : "var(--text-sm)",
                fontWeight: "var(--font-weight-semibold)",
              }}
            >
              {profile?.name
                ? profile.name
                    .split(" ")
                    .map((w: string) => w[0])
                    .join("")
                    .toUpperCase()
                    .slice(0, 2)
                : ""}
            </div>
          )}
          <div
            className={`flex-1 min-w-0 text-left transition-opacity duration-300 ${textClass}`}
          >
            <div
              className="text-duvi-text-primary truncate"
              style={{ fontSize: "var(--text-sm)" }}
            >
              {profile?.name ?? ""}
            </div>
            <div
              className="text-duvi-text-secondary truncate"
              style={{ fontSize: "var(--text-2xs)" }}
            >
              {profile?.email ?? ""}
            </div>
          </div>
        </button>

        {/* Context Menu */}
        {userMenuOpen && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setUserMenuOpen(false)}
            />
            <div className="absolute bottom-full left-2 mb-2 w-48 bg-duvi-surface border border-duvi-border rounded-[8px] shadow-lg z-50 overflow-hidden">
              <button
                onClick={() => {
                  navigate("/settings");
                  setUserMenuOpen(false);
                }}
                className="w-full text-left px-4 py-2.5 text-duvi-text-primary hover:bg-duvi-elevated transition-colors flex items-center gap-3"
                style={{ fontSize: "var(--text-sm)" }}
              >
                <Settings className="w-4 h-4 text-duvi-text-secondary" />
                Settings
              </button>
              <div className="border-t border-duvi-border" />
              <button
                onClick={() => {
                  setUserMenuOpen(false);
                  setShowLogoutDialog(true);
                }}
                data-testid="sidebar-logout"
                className="w-full text-left px-4 py-2.5 text-duvi-text-primary hover:bg-duvi-elevated transition-colors flex items-center gap-3"
                style={{ fontSize: "var(--text-sm)" }}
              >
                <LogOut className="w-4 h-4 text-duvi-text-secondary" />
                Log out
              </button>
            </div>
          </>
        )}
      </div>

      {/* Logout confirmation dialog */}
      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent className="bg-duvi-surface border-duvi-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-duvi-text-primary">
              Sign out
            </AlertDialogTitle>
            <AlertDialogDescription className="text-duvi-text-secondary">
              Are you sure you want to sign out? You'll need to sign in again to
              access the platform.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={isLoggingOut}
              className="bg-duvi-elevated border-duvi-border text-duvi-text-primary hover:bg-duvi-bg"
            >
              Cancel
            </AlertDialogCancel>
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              data-testid="logout-confirm"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-duvi-destructive px-4 py-2 text-white hover:bg-duvi-destructive/90 disabled:opacity-50 transition-colors"
              style={{ fontSize: "var(--text-sm)" }}
            >
              {isLoggingOut ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Signing out…
                </>
              ) : (
                "Yes, sign me out"
              )}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
